import * as React from 'react';
import { Dialog, Listbox, Transition, Combobox } from '@headlessui/react';
import { motion } from 'framer-motion';
import { useKeyboardInsets } from '../../hooks/useKeyboardInsets';
import { useOverlayLock } from '../../hooks/useOverlayLock';
import { useDuckDB } from '../../context/LazyDuckDBContext';
import { triggerHaptic } from '../../utils/haptics';
import { createPortal } from 'react-dom';
import { FiltersValue, CompareScope } from '../../types/filters';
import { useFilters } from '../../context/FiltersContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { MunicipalityPicker } from './MunicipalityPicker';

// --- Types ---
// Note: Types are now imported from types/filters.ts

export type FiltersSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  value: FiltersValue;
  onChange: (next: FiltersValue) => void;
};

export type Option = { id: string; name: string; disabled?: boolean; description?: string };

// Special "Alla" option for dropdowns
const ALL_OPTION = { id: 'null', name: 'Alla' };

// --- Query helpers (DuckDB) ---
// Updated to use correct table names from the marts layer
async function fetchKommuner(query: FiltersQueryFn): Promise<Option[]> {
  return query<Option[]>(
    `select distinct 
       cast(entity_id as varchar) as id, 
       entity_name as name
     from dim_entity
     where entity_type = 'municipality'
     order by name`
  );
}

async function fetchYears(query: FiltersQueryFn): Promise<number[]> {
  const rows = await query<{ year: number }[]>(
    `select distinct year 
     from dim_org_unit_year 
     order by year desc`
  );
  return rows.map(r => r.year);
}

async function fetchKpis(query: FiltersQueryFn): Promise<Option[]> {
  return query<Option[]>(
    `select cast(kpi_id as varchar) as id, kpi_title as name
     from dim_kpi
     order by kpi_title`
  );
}

async function fetchSkolor(query: FiltersQueryFn, kommunId: string | null, huvudmanId?: string | null): Promise<Option[]> {
  if (!kommunId) return [];

  const governanceFilter = huvudmanId && huvudmanId !== ''
    ? `AND gy.governance_type_year = :governance`
    : '';

  const params: Record<string, unknown> = { kommunId };
  if (huvudmanId && huvudmanId !== '') {
    params.governance = huvudmanId;
  }

  // OPTIMIZATION: Use dim_school instead of scanning heavy fact tables on S3
  const result = await query<Option[]>(
    `SELECT DISTINCT 
       s.school_id as id, 
       s.school_name as name
     FROM dim_school s
     LEFT JOIN (
       SELECT entity_id, governance_type_year 
       FROM dim_org_unit_year 
       WHERE governance_type_year IS NOT NULL
     ) gy ON gy.entity_id = s.school_id
     WHERE s.municipality_id = :kommunId
       ${governanceFilter}
     ORDER BY s.school_name`,
    params
  );

  return result;
}

// Low-level type so we can call DuckDB context in a typed way without importing its internals
type FiltersQueryFn = <T = any[]>(sql: string, params?: Record<string, unknown>) => Promise<T>;

// --- Hook to load options (with shared state in FiltersContext) ---
function useFilterOptions(kommunId: string | null, huvudmanId?: string | null) {
  const { query, ready, isInitializing } = useDuckDB();
  const { options, setOptions } = useFilters();

  // Initial load of global options (kommuner, years, kpis)
  React.useEffect(() => {
    let cancelled = false;
    async function loadGlobalOptions() {
      // If already loaded or loading, skip
      if (!ready || options.kommuner.length > 0) return;

      setOptions({ loading: true });
      try {
        const [kList, yList, kpiList] = await Promise.all([
          fetchKommuner(query),
          fetchYears(query),
          fetchKpis(query),
        ]);
        if (!cancelled) {
          setOptions({
            kommuner: kList,
            years: yList,
            kpis: kpiList,
            loading: false
          });
        }
      } catch (err) {
        console.error('Failed to load global filter options:', err);
        if (!cancelled) setOptions({ loading: false });
      }
    }
    loadGlobalOptions();
    return () => { cancelled = true; };
  }, [ready, query, options.kommuner.length, setOptions]);

  // Load dependent options (skolor) when kommunId or huvudmanId changes
  // We keep this local to the hook or in context? 
  // Let's keep schools in context too but refresh them when kommun/huvudman changes.
  React.useEffect(() => {
    let cancelled = false;
    async function loadSkolor() {
      if (!ready || !kommunId) {
        if (!cancelled) setOptions({ skolor: [ALL_OPTION] });
        return;
      }

      try {
        const list = await fetchSkolor(query, kommunId, huvudmanId);
        if (!cancelled) {
          setOptions({ skolor: [ALL_OPTION, ...list] });
        }
      } catch (err) {
        console.error('Failed to load schools for filter:', err);
      }
    }
    loadSkolor();
    return () => { cancelled = true; };
  }, [ready, query, kommunId, huvudmanId, setOptions]);

  return {
    kommuner: options.kommuner,
    skolor: options.skolor,
    kpis: options.kpis,
    years: options.years,
    loading: options.loading || isInitializing,
    dbReady: ready,
  };
}

// --- UI primitives ---
function FieldLabel({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <label htmlFor={id} className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">
      {children}
    </label>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function TapListbox<T extends { id: string; name: string }>(props: {
  id: string;
  options: T[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const selected = props.options.find(o => o.id === props.value) ?? null;
  return (
    <Listbox
      value={selected}
      onChange={(opt: T | null) => props.onChange(opt ? opt.id : null)}
      disabled={props.disabled}
    >
      <div className="relative">
        <Listbox.Button
          id={props.id}
          className={`min-h-11 w-full rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-left text-base text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400 ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
        >
          {selected ? selected.name : (props.placeholder ?? 'Välj…')}
        </Listbox.Button>
        <Transition
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-10 mt-2 max-h-64 w-full overflow-auto rounded-xl bg-white dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-700 p-1 shadow-lg filters-sheet-options">
            {props.options.map(opt => (
              <Listbox.Option key={opt.id} value={opt} className={({ active }) =>
                `cursor-pointer select-none rounded-lg px-3 py-3 text-base text-slate-900 dark:text-slate-100 ${active ? 'bg-emerald-50 dark:bg-emerald-900/40' : ''}`
              }>
                {opt.name}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

function SegmentedScope({ value, onChange, kpiId }: { value: CompareScope; onChange: (v: CompareScope) => void; kpiId?: string | null }) {
  // Only N15504 (Meritvärde) supports SALSA - otherwise disable it
  const supportsSalsa = kpiId === 'N15504';

  const items: { id: CompareScope; label: string; disabled?: boolean }[] = [
    { id: 'municipality', label: 'Kommun' },
    { id: 'national', label: 'Riket' },
    { id: 'salsa', label: 'SALSA', disabled: !supportsSalsa },
  ];

  return (
    <div role="tablist" aria-label="Jämförelse" className="grid grid-cols-3 gap-2">
      {items.map(item => {
        const active = item.id === value;
        const disabled = item.disabled;
        return (
          <button
            key={item.id}
            role="tab"
            aria-selected={active}
            disabled={disabled}
            onClick={() => !disabled && onChange(item.id)}
            className={`min-h-11 rounded-xl px-4 py-3 text-base ring-1 ring-slate-200 dark:ring-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400 transition-all ${disabled
              ? 'opacity-40 cursor-not-allowed bg-slate-50 dark:bg-slate-900 text-slate-400 dark:text-slate-600'
              : active
                ? 'bg-emerald-600 text-white'
                : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            title={disabled ? 'Endast tillgänglig för Meritvärde (N15504)' : undefined}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

// KPI Combobox with search and desktop portal
function KpiCombo({
  options, value, onChange, onOpen,
}: {
  options: Option[];
  value: string | null;
  onChange: (id: string | null) => void;
  onOpen?: (open: boolean) => void;
}) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [query, setQuery] = React.useState("");
  const [focused, setFocused] = React.useState(false);
  const selected = options.find(o => o.id === value) ?? null;
  const ref = React.useRef<HTMLDivElement>(null);
  const [rect, setRect] = React.useState<DOMRect | null>(null);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => o.name.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const open = focused && (query.length > 0 || !selected);

  React.useEffect(() => {
    if (open && isDesktop) {
      setRect(ref.current?.getBoundingClientRect() ?? null);
    }
    onOpen?.(open);
  }, [open, isDesktop, onOpen]);

  return (
    <Combobox
      value={selected}
      onChange={(opt: Option | null) => {
        onChange(opt?.id ?? null);
        setQuery("");
        setFocused(false);
        onOpen?.(false);
      }}
    >
      <div className="relative" ref={ref}>
        <Combobox.Input
          onFocus={() => {
            setFocused(true);
            if (isDesktop) {
              setRect(ref.current?.getBoundingClientRect() ?? null);
            }
          }}
          onBlur={() => setFocused(false)}
          onChange={(e) => setQuery(e.target.value)}
          displayValue={(o: Option | null) => o?.name ?? ""}
          placeholder="Välj indikator…"
          className="h-11 w-full rounded-xl ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-800 px-4 text-base text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400"
        />

        {/* Clear button */}
        {(query || selected) && (
          <button
            type="button"
            aria-label="Rensa"
            onClick={() => {
              setQuery("");
              onChange(null);
              setFocused(true);
            }}
            className="absolute inset-y-0 right-2 my-auto rounded-md px-2 py-1 text-sm text-zinc-600 hover:bg-zinc-100"
          >
            ×
          </button>
        )}

        {open && (
          <>
            {isDesktop ? (
              // Portal so it won't be clipped by the card
              createPortal(
                <Combobox.Options
                  static
                  className="fixed z-[60] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-2xl ring-1 ring-black/5"
                  style={{
                    left: rect?.left ?? 0,
                    top: (rect?.bottom ?? 0) + 8,
                    width: rect?.width,
                    maxHeight: "min(60dvh, 520px)",  // tall on desktop
                  }}
                >
                  <div className="sticky top-0 bg-white dark:bg-slate-800 px-2 pt-2 pb-2 border-b border-slate-200 dark:border-slate-700">
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Sök indikator…"
                      className="h-9 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  {filtered.length === 0 && (
                    <div className="px-3 py-3 text-sm text-slate-600 dark:text-slate-400">Ingen träff</div>
                  )}
                  {filtered.map(opt => (
                    <Combobox.Option
                      key={opt.id}
                      value={opt}
                      className={({ active, selected }) =>
                        `h-11 flex items-center justify-between px-4 py-3 cursor-pointer select-none rounded-lg text-base text-slate-900 dark:text-slate-100 ${active ? "bg-emerald-50 dark:bg-emerald-900/40" : ""
                        } ${selected ? "font-medium bg-emerald-50/50 dark:bg-emerald-900/20" : ""}`
                      }
                    >
                      <span className="truncate">{opt.name}</span>
                      {selected && <span aria-hidden className="text-lg">✓</span>}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>,
                document.body
              )
            ) : (
              // Mobile stays inline
              <Combobox.Options
                static
                className="mt-2 max-h-[40dvh] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg"
              >
                {filtered.length === 0 && (
                  <div className="px-3 py-3 text-sm text-slate-600 dark:text-slate-400">Ingen träff</div>
                )}
                {filtered.map(opt => (
                  <Combobox.Option
                    key={opt.id}
                    value={opt}
                    className={({ active, selected }) =>
                      `h-11 flex items-center justify-between px-4 py-3 cursor-pointer select-none rounded-lg text-base text-slate-900 dark:text-slate-100 ${active ? "bg-emerald-50 dark:bg-emerald-900/40" : ""
                      } ${selected ? "font-medium bg-emerald-50/50 dark:bg-emerald-900/20" : ""}`
                    }
                  >
                    <span className="truncate">{opt.name}</span>
                    {selected && <span aria-hidden className="text-lg">✓</span>}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
          </>
        )}
      </div>
    </Combobox>
  );
}

export function FiltersSheet({ isOpen, onClose, value, onChange }: FiltersSheetProps) {
  useKeyboardInsets();
  useOverlayLock(isOpen); // Lock body scroll when sheet is open (robust iOS-compatible method)
  // Use draft.kommunId to drive dependent school list loading
  const [draft, setDraft] = React.useState<FiltersValue>(value);
  const { kommuner, skolor, kpis, years, loading, dbReady } = useFilterOptions(draft.kommunId, draft.huvudmanId);

  // Suppress ResizeObserver errors (they're typically harmless)
  React.useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.stopImmediatePropagation();
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    return () => window.removeEventListener('error', handleResizeObserverError);
  }, []);

  // Track which combobox is open so only one expands at a time
  const [openId, setOpenId] = React.useState<string | null>(null);
  // Track open list height for bottom padding
  const [openListPad, setOpenListPad] = React.useState(0);
  // Track KPI dropdown state for desktop panel sizing
  const [kpiOpen, setKpiOpen] = React.useState(false);
  // Track municipality picker dialog state
  const [kommunPickerOpen, setKommunPickerOpen] = React.useState(false);

  // Sync draft when opening or when external value changes while closed
  React.useEffect(() => {
    if (isOpen) {
      setDraft(value);
    }
  }, [isOpen, value]);

  // No longer hiding maps, using backdrop blur instead for better UX
  React.useEffect(() => {
    return () => {
      // Cleanup if needed
    };
  }, [isOpen]);

  // Add scrollbar styling for dark mode
  React.useEffect(() => {
    if (isOpen) {
      const style = document.createElement('style');
      style.textContent = `
        .filters-sheet-options::-webkit-scrollbar {
          width: 6px;
        }
        .filters-sheet-options::-webkit-scrollbar-track {
          background: transparent;
        }
        .filters-sheet-options::-webkit-scrollbar-thumb {
          background: rgb(203 213 225); /* slate-300 */
          border-radius: 3px;
        }
        .dark .filters-sheet-options::-webkit-scrollbar-thumb {
          background: rgb(71 85 105); /* slate-600 */
        }
      `;
      document.head.appendChild(style);
      return () => {
        if (document.head.contains(style)) {
          document.head.removeChild(style);
        }
      };
    }
  }, [isOpen]);

  // Ensure draft.skolaId remains valid if kommun or options change
  React.useEffect(() => {
    if (draft.skolaId && skolor.length && !skolor.some(s => s.id === draft.skolaId)) {
      setDraft(prev => ({ ...prev, skolaId: null }));
    }
  }, [skolor, draft.skolaId]);

  const latestYear = React.useMemo(() => years[0] ?? value.year ?? null, [years, value.year]);

  function handleReset() {
    // Close any open dropdowns first to prevent ResizeObserver issues
    setOpenId(null);
    setOpenListPad(0);

    // Use setTimeout to defer the state update to the next tick
    setTimeout(() => {
      setDraft({
        kommunId: 'null',
        huvudmanId: null,
        skolaId: 'null',
        year: latestYear,
        kpiId: null,
        compareScope: 'municipality',
        activeTab: null,
        governance: '',
      });
    }, 0);
  }

  function handleApply() {
    triggerHaptic('light');
    onChange(draft);
    onClose();
  }

  function handleCancel() {
    triggerHaptic('light');
    onClose();
  }

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-[9999]">
        {/* Fixed overlay with blur */}
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-md" aria-hidden="true" />

        {/* Fixed panel container */}
        <div className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-6 md:py-16 lg:py-24">
          <Dialog.Panel className={[
            "w-full overflow-hidden bg-white shadow-2xl ring-1 ring-black/5 md:rounded-2xl",
            "sm:max-w-xl rounded-t-3xl sm:rounded-2xl",
            kpiOpen
              ? "md:max-w-3xl lg:max-w-4xl md:max-h-[90dvh]"   // bigger when KPI is open
              : "md:max-w-2xl md:max-h-[82dvh]"
          ].join(" ")}>
            <motion.div
              className={[
                "w-full bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-2xl md:rounded-2xl md:shadow-2xl md:ring-1 md:ring-black/5 flex flex-col",
                "max-h-[90dvh] sm:max-h-[85dvh]",
                kpiOpen ? "md:max-h-[90dvh]" : "md:max-h-[82dvh]"
              ].join(" ")}
              style={{ overscrollBehavior: 'contain' }}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 120) onClose();
              }}
            >
              {/* Grabber */}
              <div
                className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-slate-300 dark:bg-slate-600 sm:hidden"
                role="button"
                aria-label="Dra ner för att stänga"
                tabIndex={-1}
              />

              {/* Sticky header */}
              <div className="sticky top-0 z-10 px-4 sm:px-6 md:px-6 pt-[env(safe-area-inset-top)] pb-3 md:pt-5 md:pb-3 border-b border-slate-200/60 dark:border-slate-700/60 md:border-slate-200/70 bg-white/80 dark:bg-slate-800/80 backdrop-blur rounded-t-3xl sm:rounded-t-2xl md:rounded-t-2xl">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <Dialog.Title className="text-lg font-semibold text-slate-900 dark:text-slate-100">Filter</Dialog.Title>
                      <button
                        onClick={handleReset}
                        className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 underline decoration-dotted underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded px-1"
                        aria-label="Återställ filter"
                      >
                        Återställ
                      </button>
                    </div>
                    <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">Välj år, kommun och skola.</p>
                  </div>
                  <button
                    onClick={handleCancel}
                    aria-label="Stäng"
                    className="h-11 rounded-xl px-4 text-base bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400"
                  >
                    Stäng
                  </button>
                </div>
              </div>

              {/* Body */}
              <form
                className="px-4 sm:px-6 md:px-6 py-4 md:py-5 overflow-y-auto overscroll-contain space-y-5 md:space-y-6 flex-1"
                style={{
                  paddingBottom: `calc(max(var(--kb, 0px), 40px) + ${openListPad}px)`,
                  scrollPaddingBottom: `calc(max(var(--kb, 0px), 120px) + ${openListPad}px)`,
                  scrollPaddingTop: '20px'
                }}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleApply();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") handleCancel();
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleApply();
                  }
                }}
              >
                {/* Loading / skeleton */}
                {!dbReady || loading ? (
                  <div className="mt-2 space-y-4">
                    {[0, 1, 2, 3].map(i => (
                      <div key={i} className="h-11 rounded-xl bg-slate-200 dark:bg-slate-800 animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Desktop grid layout */}
                    <div className="md:grid md:grid-cols-2 md:gap-4 md:items-start space-y-5 md:space-y-0">
                      {/* År */}
                      <div className="md:col-span-1">
                        <FieldLabel id="year">År</FieldLabel>
                        <Listbox value={draft.year} onChange={(year: number | null) => setDraft(prev => ({ ...prev, year }))}>
                          <div className="relative">
                            <Listbox.Button id="year" className="h-11 w-full rounded-xl ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-800 px-4 text-left text-base text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400">
                              {draft.year ?? 'Välj år…'}
                            </Listbox.Button>
                            <Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                              <Listbox.Options className="absolute z-10 mt-2 max-h-[60dvh] sm:max-h-64 w-full overflow-auto rounded-xl bg-white dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-700 p-1 shadow-lg filters-sheet-options">
                                {years.map(y => (
                                  <Listbox.Option key={y} value={y} className={({ active, selected }) => `min-h-[48px] cursor-pointer select-none rounded-lg px-4 py-3 text-base text-slate-900 dark:text-slate-100 ${active ? 'bg-emerald-50 dark:bg-emerald-900/40' : ''} ${selected ? 'font-medium bg-emerald-50/50 dark:bg-emerald-900/20' : ''}`}>
                                    {y}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>

                      {/* Jämförelse - HIDDEN */}
                      {false && (
                        <div className="md:col-span-1">
                          <FieldLabel id="compare">Jämförelse</FieldLabel>
                          <SegmentedScope
                            value={draft.compareScope}
                            onChange={(compareScope: CompareScope) => setDraft(prev => ({ ...prev, compareScope }))}
                            kpiId={draft.kpiId}
                          />
                        </div>
                      )}

                      {/* Kommun (Full-screen picker) */}
                      <div className="md:col-span-2">
                        <FieldLabel id="kommun">Kommun</FieldLabel>
                        <p className="mt-1.5 text-xs text-slate-500">Välj kommun (obligatoriskt)</p>
                        <button
                          type="button"
                          onClick={() => setKommunPickerOpen(true)}
                          className="h-11 w-full rounded-xl ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-800 px-4 text-left text-base text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400 transition-colors"
                          aria-label="Välj kommun"
                        >
                          {draft.kommunId && draft.kommunId !== 'null'
                            ? kommuner.find(k => k.id === draft.kommunId)?.name
                            : <span className="text-slate-500">Sök kommun…</span>
                          }
                        </button>
                      </div>

                      {/* Huvudman (Governance) */}
                      <div className="md:col-span-2">
                        <FieldLabel id="governance">Huvudman</FieldLabel>
                        <select
                          id="governance"
                          value={draft.huvudmanId || ''}
                          onChange={(e) => setDraft(prev => ({ ...prev, huvudmanId: e.target.value || null, skolaId: null }))}
                          className="h-11 w-full rounded-xl ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-800 px-4 text-base text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400"
                        >
                          <option value="">Alla</option>
                          <option value="kommunal">Kommunal</option>
                          <option value="fristående">Fristående</option>
                        </select>
                      </div>

                      {/* Skola (Combobox, depends on kommun and huvudman) - TEMPORARILY DISABLED */}
                      {false && (
                        <div className="md:col-span-2">
                          <FieldLabel id="skola">Skola</FieldLabel>
                          <ComboSearch
                            id="skola"
                            options={skolor}
                            value={draft.skolaId}
                            onChange={(skolaId) => {
                              if (skolaId === 'null' || !skolaId) {
                                setDraft(prev => ({ ...prev, skolaId, kpiId: null, compareScope: 'municipality' }));
                              } else {
                                setDraft(prev => ({ ...prev, skolaId }));
                              }
                            }}
                            disabled={!draft.kommunId}
                            openId={openId}
                            setOpenId={setOpenId}
                            onOpenChange={(isOpen, listPx) => setOpenListPad(isOpen ? listPx : 0)}
                            onEnter={(firstMatch, q) => {
                              // If no match → default to Alla and APPLY immediately
                              const chosenId = firstMatch ? firstMatch.id : 'null';
                              setDraft(prev => ({ ...prev, skolaId: chosenId }));
                              handleApply();
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* KPI (only when school is selected) */}
                    {draft.skolaId && draft.skolaId !== 'null' && (
                      <div className="md:col-span-2">
                        <FieldLabel id="kpi">Indikator (KPI)</FieldLabel>
                        <KpiCombo
                          options={kpis}
                          value={draft.kpiId ?? null}
                          onChange={(kpiId) => setDraft(prev => ({ ...prev, kpiId }))}
                          onOpen={(open) => setKpiOpen(open)}
                        />
                      </div>
                    )}
                  </>
                )}
              </form>

              {/* Sticky footer actions */}
              <div
                className="sticky z-10 px-4 sm:px-6 md:px-6 pt-3 md:py-4 border-t border-slate-200/60 dark:border-slate-700/60 md:border-slate-200/70 bg-white/80 dark:bg-slate-800/80 md:bg-white/85 backdrop-blur rounded-b-3xl sm:rounded-b-2xl md:rounded-b-2xl transition-all duration-200"
                style={{
                  bottom: `var(--kb, 0px)`,
                  paddingBottom: `max(env(safe-area-inset-bottom), 1rem)`
                }}
              >
                {/* Selection summary */}
                {draft.kommunId && draft.kommunId !== 'null' && (
                  <div className="mb-3 text-sm text-slate-600 dark:text-slate-400">
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {kommuner.find(k => k.id === draft.kommunId)?.name || 'Vald kommun'}
                    </span>
                    {draft.year && <span> • {draft.year}</span>}
                    {draft.huvudmanId && <span> • {draft.huvudmanId === 'kommunal' ? 'Kommunal' : 'Fristående'}</span>}
                  </div>
                )}
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={handleCancel}
                    className="h-11 rounded-xl px-5 text-base bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={handleApply}
                    className="h-11 rounded-xl px-6 text-base font-medium bg-emerald-600 text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                  >
                    Verkställ
                  </button>
                </div>
              </div>
            </motion.div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Municipality full-screen picker */}
      <MunicipalityPicker
        isOpen={kommunPickerOpen}
        onClose={() => setKommunPickerOpen(false)}
        options={kommuner.map(k => {
          const isRegion = k.id.length === 2 || k.id.startsWith('00');
          return {
            ...k,
            disabled: isRegion,
            description: isRegion ? 'Län / Region' : undefined
          };
        })}
        selectedId={draft.kommunId}
        onSelect={(kommunId) => {
          triggerHaptic('light');
          setDraft(prev => ({
            ...prev,
            kommunId,
            huvudmanId: null, // Clear huvudman when kommun changes
            skolaId: null,
            kpiId: null,
            compareScope: 'municipality',
          }));
        }}
      />
    </Transition>
  );
}

// Simple Combobox with type-to-search, no virtualization to keep deps lean
function ComboSearch<T extends { id: string; name: string }>(props: {
  id: string;
  options: T[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  openId: string | null;
  setOpenId: (id: string | null) => void;
  onEnter?: (firstMatch: T | null, query: string) => void;
  onOpenChange?: (isOpen: boolean, listPx: number) => void;
}) {
  const [query, setQuery] = React.useState("");
  const [focused, setFocused] = React.useState(false);
  const selected = React.useMemo(() => props.options.find(o => o.id === props.value) ?? null, [props.options, props.value]);
  const isWide = useMediaQuery("(min-width: 768px)"); // md and up → 7 rows
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Direct query usage for instant feedback (list is small enough < 300 items)
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? props.options.filter(o => o.name.toLowerCase().includes(q)) : props.options;
  }, [props.options, query]);

  // OPEN when this field is focused/active
  const open = props.openId === props.id && focused;

  // Exactly 3 rows on mobile, 7 on ≥md
  const ROW_H = 48; // h-12
  const VISIBLE = isWide ? 7 : 3;
  const LIST_H = ROW_H * VISIBLE;

  /* eslint-disable react-hooks/exhaustive-deps */
  // props.onOpenChange intentionally excluded to avoid re-render loop
  React.useEffect(() => {
    props.onOpenChange?.(open, open ? LIST_H : 0);
    // Scrolling is now handled in onFocus to avoid race conditions with keyboard
  }, [open, LIST_H]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <Combobox
      value={selected}
      onChange={(opt: T | null) => {
        // Selection made
        props.onChange(opt ? opt.id : null);
        props.setOpenId(null);
        setQuery("");             // Reset query
        setFocused(false);
        props.onOpenChange?.(false, 0);
      }}
      disabled={props.disabled}
      nullable
    >
      <div className="relative" ref={containerRef}>
        {/* Search icon */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 dark:text-slate-500">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <Combobox.Input
          id={props.id}
          ref={inputRef}
          className="h-11 w-full rounded-xl ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-800 pl-10 pr-4 text-base text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-3 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400 focus-visible:ring-offset-2"
          placeholder={props.placeholder ?? "Sök…"}
          displayValue={(opt: T | null) => opt ? opt.name : ""}
          aria-label={props.placeholder || "Sök alternativ"}
          aria-describedby={`${props.id}-search-help`}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-haspopup="listbox"
          onFocus={() => {
            props.setOpenId(props.id);
            setFocused(true);
            // Scroll input into view when keyboard appears
            // Multiple attempts with increasing delays to handle iOS keyboard animation
            setTimeout(() => {
              inputRef.current?.scrollIntoView({
                block: "center",
                behavior: "smooth"
              });
            }, 200);
            setTimeout(() => {
              inputRef.current?.scrollIntoView({
                block: "center",
                behavior: "auto"
              });
            }, 500);
          }}
          onBlur={() => {
            // Small delay to allow click on options to register
            setTimeout(() => setFocused(false), 150);
          }}
          onChange={(e) => {
            const val = e.target.value;
            setQuery(val);

            // CRITICAL: Always set these when typing to ensure dropdown stays open
            // This fixes the issue where manually deleting all chars doesn't show suggestions
            props.setOpenId(props.id);
            setFocused(true);
            props.onOpenChange?.(true, LIST_H);

            // If user clears input, clear selection too
            if (val === '') {
              props.onChange(null);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              props.onEnter?.(filtered[0] ?? null, query);
            }
          }}
          // make sure there's space to scroll the field into view
          style={{
            scrollMarginBottom: `calc(var(--kb,0px) + ${LIST_H + 60}px)`,
            scrollMarginTop: '80px'
          }}
        />

        {/* Accessibility help text */}
        <div id={`${props.id}-search-help`} className="sr-only">
          Använd piltangenterna för att navigera genom alternativen. Tryck på Enter för att välja.
        </div>

        {/* ARIA Live Region for announcements */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          id={`${props.id}-announcements`}
        >
          {filtered.length > 0
            ? `${filtered.length} alternativ tillgängliga`
            : 'Inga alternativ hittades'
          }
        </div>

        {/* Clear */}
        {(query || selected) && (
          <button
            type="button"
            aria-label="Rensa"
            onClick={() => {
              setQuery("");
              props.onChange(null);
              props.setOpenId(props.id);
              setFocused(true);
              inputRef.current?.focus();
              props.onOpenChange?.(true, LIST_H);
            }}
            className="absolute inset-y-0 right-2 my-auto rounded-md px-2 py-1 text-sm text-zinc-600 hover:bg-zinc-100"
          >
            ×
          </button>
        )}

        {/* Options — inline on mobile, popover on desktop */}
        {open && (
          <Combobox.Options
            static={!isWide}
            onPointerDownCapture={(e) => e.stopPropagation()} // don't start sheet drag
            className={isWide
              ? "absolute left-0 right-0 top-full mt-2 z-50 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl ring-1 ring-black/5 filters-sheet-options"
              : "mt-2 w-full overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg filters-sheet-options touch-pan-y"
            }
            style={{
              maxHeight: `${LIST_H}px`,
              minHeight: `${Math.min(filtered.length, VISIBLE) * ROW_H}px`,
            }}
          >
            {/* Result count */}
            {query && filtered.length > 0 && (
              <div className="sticky top-0 bg-white dark:bg-slate-800 px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                {filtered.length} {props.id === 'kommun' ? 'kommuner' : props.id === 'skola' ? 'skolor' : 'alternativ'}
              </div>
            )}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-600 dark:text-slate-400">Ingen träff</div>
            )}
            {filtered.map(opt => (
              <Combobox.Option
                key={opt.id}
                value={opt}
                className={({ active, selected }) =>
                  `h-12 flex items-center justify-between cursor-pointer select-none rounded-lg px-4 py-3 text-base text-slate-900 dark:text-slate-100 ${active ? "bg-emerald-50 dark:bg-emerald-900/40" : ""
                  } ${selected ? "font-medium bg-emerald-50/50 dark:bg-emerald-900/20" : ""}`
                }
              >
                <span className="truncate">{opt.name}</span>
                {selected && <span aria-hidden="true" className="text-lg">✓</span>}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}